import { SkuTableDataType } from "./skuTableUtils";
import { downloadCSV, convertToCSV } from "../configurationPageUtils";

/**
 * Helper function to convert time unit to milliseconds
 * e.g., "hour" -> 3600000
 */
const convertTimeUnitToMs = (timeUnit: string | null | undefined): string => {
  if (!timeUnit) {
    return "";
  }

  const unitLower = timeUnit.toLowerCase().trim();

  if (unitLower === "hour") {
    return "3600000";
  }
  if (unitLower === "minute") {
    return "60000";
  }
  if (unitLower === "second") {
    return "1000";
  }

  // Handle plural forms
  if (unitLower.includes("hour")) {
    return "3600000";
  }
  if (unitLower.includes("minute")) {
    return "60000";
  }
  if (unitLower.includes("second")) {
    return "1000";
  }

  // If already a millisecond value, return as is
  const MILLIS_REGEX = /^\d+$/; // matches a string consisting solely of one or more digits
  if (MILLIS_REGEX.test(unitLower)) {
    return unitLower;
  }

  return "";
};

/**
 * Prepares SKU table data for CSV export
 * Splits the combined target rate column into separate rate and timeWindow columns
 * Uses headers that match the table headers (in camelCase)
 */
export const prepareSkuDataForExport = (
  data: SkuTableDataType[]
): Record<string, any>[] => {
  return data.map(sku => {
    // Get the combined target rate value (e.g., "240.0 / hour")
    const combinedTargetRate = sku.targetRate?.toString() || "";
    const rawTimeWindow = sku.timeWindow?.toString() || "";

    let rate = "";
    let timeWindow = "";

    // First check if we have a direct timeWindow value available
    if (rawTimeWindow === "3600000") {
      timeWindow = "3600000"; // hour
    } else if (rawTimeWindow === "60000") {
      timeWindow = "60000"; // minute
    } else if (rawTimeWindow === "1000") {
      timeWindow = "1000"; // second
    } else if (rawTimeWindow) {
      timeWindow = rawTimeWindow;
    }

    // Then check the combined rate format
    if (combinedTargetRate.includes("/")) {
      // Split "rate / timeUnit" into separate values
      const parts = combinedTargetRate.split("/");
      rate = parts[0].trim();
      const timeUnit = parts[1].trim();

      // Only override timeWindow if we don't already have it
      if (!timeWindow) {
        timeWindow = convertTimeUnitToMs(timeUnit);
      }
    } else {
      // No time unit specified, use just the rate
      rate = combinedTargetRate;
      // timeWindow was already set above if it exists
    }

    // Try to extract timeWindow from the row.rate string if it contains a value like "960.0 / 3600000"
    const rateString = sku.rate?.toString() || "";
    if (!timeWindow && rateString.includes("/")) {
      const parts = rateString.split("/");
      if (parts.length > 1) {
        const possibleTimeWindow = parts[1].trim();
        if (possibleTimeWindow === "3600000" || possibleTimeWindow === "hour") {
          timeWindow = "3600000";
        } else if (
          possibleTimeWindow === "60000" ||
          possibleTimeWindow === "minute"
        ) {
          timeWindow = "60000";
        } else if (
          possibleTimeWindow === "1000" ||
          possibleTimeWindow === "second"
        ) {
          timeWindow = "1000";
        } else {
          timeWindow = possibleTimeWindow;
        }
      }
    }

    // Calculate team size min and max instead of concatenating all team sizes
    let teamSizeMin = "";
    let teamSizeMax = "";

    if (Array.isArray(sku.teamSizes) && sku.teamSizes.length > 0) {
      // Sort team sizes to find min and max
      const sortedTeamSizes = [...sku.teamSizes].sort((a, b) => a - b);
      teamSizeMin = sortedTeamSizes[0].toString();
      teamSizeMax = sortedTeamSizes[sortedTeamSizes.length - 1].toString();
    }

    return {
      // Rename headers to match table headers but in camelCase
      processName: sku.stationName || "",
      skuId: sku.skuCode || "",
      skuDescription: sku.skuName || "",
      targetRate: rate,
      timeWindow: timeWindow,
      skuScale: sku.scale || "",
      teamSizeMin: teamSizeMin,
      teamSizeMax: teamSizeMax,
      linearScaling: sku.isLinearScaling ? "Yes" : "No",
    };
  });
};

/**
 * Exports SKU table data to CSV
 */
export const exportSkuTableToCsv = (
  data: SkuTableDataType[],
  filename: string = "sku_table_export.csv"
): void => {
  try {
    // Prepare data for export with split columns
    const exportData = prepareSkuDataForExport(data);

    if (exportData.length > 0) {
      const csvContent = convertToCSV(exportData);
      downloadCSV(csvContent, filename);
    } else {
      console.warn("No data to export");
    }
  } catch (error) {
    console.error("Error exporting SKU table data:", error);
  }
};
